import {
  ensureSeedData,
  openDatabase,
  readAllFromStore,
  STORE_BUDGET_CATEGORIES,
  STORE_EXPENSES,
  STORE_PROFILES,
  STORE_YEARS,
  transactionDone
} from "../../lib/storage/walletWatchDbCore.js";
import {
  normalizeProfileRecord,
  setActiveBudgetScope
} from "../../lib/storage/walletWatchProfiles.js";

export const PROFILE_DATA_EXPORT_VERSION = 1;

export function buildProfileDataExportText(payload) {
  return `${JSON.stringify(payload, null, 2)}\n`;
}

function buildImportedProfileName(baseName, existingNames) {
  const trimmedBaseName = String(baseName ?? "").trim() || "Imported Profile";
  const directCandidate = `${trimmedBaseName} (Imported)`;

  if (!existingNames.has(directCandidate.toLocaleLowerCase())) {
    return directCandidate;
  }

  let suffix = 2;
  while (existingNames.has(`${directCandidate} ${suffix}`.toLocaleLowerCase())) {
    suffix += 1;
  }

  return `${directCandidate} ${suffix}`;
}

function normalizeYearEntries(payload) {
  const entriesByYear = new Map();

  payload.years?.forEach((entry) => {
    const year = String(entry?.year ?? "").trim();

    if (!year) {
      return;
    }

    entriesByYear.set(year, {
      year,
      archivedAt: entry?.archivedAt ?? (entry?.archived ? new Date().toISOString() : null)
    });
  });

  [payload.activeYear]
    .concat(payload.budgets?.map((entry) => entry?.year) ?? [])
    .concat(payload.expenses?.map((entry) => entry?.year) ?? [])
    .forEach((value) => {
      const year = String(value ?? "").trim();

      if (!year || entriesByYear.has(year)) {
        return;
      }

      entriesByYear.set(year, {
        year,
        archivedAt: null
      });
    });

  return Array.from(entriesByYear.values()).sort((left, right) => String(left.year).localeCompare(String(right.year)));
}

function validateProfileDataExportPayload(payload) {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    throw new Error("Profile data file is not a valid JSON object.");
  }

  if (payload.version !== PROFILE_DATA_EXPORT_VERSION) {
    throw new Error(`Profile data export version ${payload.version ?? "unknown"} is not supported.`);
  }

  if (!payload.profile || typeof payload.profile !== "object") {
    throw new Error("Profile data file is missing profile information.");
  }

  const profileName = String(payload.profile.name ?? "").trim();
  if (!profileName) {
    throw new Error("Profile data file profile name is missing.");
  }

  if (!Array.isArray(payload.years)) {
    throw new Error("Profile data file is missing the years list.");
  }

  if (!Array.isArray(payload.budgets)) {
    throw new Error("Profile data file is missing the budget list.");
  }

  if (!Array.isArray(payload.expenses)) {
    throw new Error("Profile data file is missing the expense list.");
  }

  const years = normalizeYearEntries(payload);
  if (years.length === 0) {
    throw new Error("Profile data file does not include any usable year.");
  }

  const yearsSet = new Set(years.map((entry) => entry.year));
  const activeYear = yearsSet.has(String(payload.activeYear ?? "").trim())
    ? String(payload.activeYear ?? "").trim()
    : years[years.length - 1].year;

  return {
    profile: {
      name: profileName,
      source: String(payload.profile.source ?? "import").trim() || "import"
    },
    activeYear,
    years,
    budgets: payload.budgets,
    expenses: payload.expenses
  };
}

export async function restoreProfileDataExport(payload, options = {}) {
  await ensureSeedData();
  const profileData = validateProfileDataExportPayload(payload);
  const db = await openDatabase();
  const transaction = db.transaction(
    [STORE_PROFILES, STORE_YEARS, STORE_BUDGET_CATEGORIES, STORE_EXPENSES],
    "readwrite"
  );
  const profilesStore = transaction.objectStore(STORE_PROFILES);
  const yearsStore = transaction.objectStore(STORE_YEARS);
  const budgetCategoriesStore = transaction.objectStore(STORE_BUDGET_CATEGORIES);
  const expensesStore = transaction.objectStore(STORE_EXPENSES);
  const existingProfiles = await readAllFromStore(profilesStore);
  const existingNames = new Set(
    existingProfiles
      .filter((profile) => !profile.archivedAt)
      .map((profile) => String(profile.name ?? "").trim().toLocaleLowerCase())
      .filter(Boolean)
  );
  const now = new Date().toISOString();
  const profileId = `profile-${crypto.randomUUID()}`;
  const profileName = buildImportedProfileName(profileData.profile.name, existingNames);
  const validYears = new Set(profileData.years.map((entry) => entry.year));
  const categoryIdsByLegacyId = new Map();
  const categoryIdsByYearName = new Map();

  profilesStore.put({
    id: profileId,
    name: profileName,
    source: options.source ?? "import",
    isDefault: false,
    archivedAt: null,
    createdAt: now,
    updatedAt: now
  });

  profileData.years.forEach((entry) => {
    yearsStore.put({
      id: `${profileId}:${entry.year}`,
      profileId,
      year: entry.year,
      archivedAt: entry.archivedAt ?? null,
      createdAt: now,
      updatedAt: now
    });
  });

  profileData.budgets.forEach((yearEntry) => {
    const year = String(yearEntry?.year ?? "").trim();

    if (!validYears.has(year) || !Array.isArray(yearEntry?.rows)) {
      return;
    }

    yearEntry?.rows?.forEach((row) => {
      const nextId = crypto.randomUUID();
      const categoryName = String(row?.category ?? "").trim();

      if (!categoryName) {
        return;
      }

      categoryIdsByLegacyId.set(String(row?.id ?? ""), nextId);
      categoryIdsByYearName.set(`${year}::${categoryName.toLocaleLowerCase()}`, nextId);

      budgetCategoriesStore.put({
        id: nextId,
        profileId,
        year,
        name: categoryName,
        ongoingBudget: Number(row?.ongoingBudget ?? 0),
        irregularBudget: Number(row?.irregularBudget ?? 0),
        archivedAt: row?.archivedAt ?? null,
        createdAt: now,
        updatedAt: now
      });
    });
  });

  profileData.expenses.forEach((yearEntry) => {
    const year = String(yearEntry?.year ?? "").trim();

    if (!validYears.has(year) || !Array.isArray(yearEntry?.rows)) {
      return;
    }

    yearEntry.rows.forEach((row) => {
      const categoryName = String(row?.category ?? "").trim();
      const categoryId = categoryIdsByLegacyId.get(String(row?.categoryId ?? ""))
        ?? categoryIdsByYearName.get(`${year}::${categoryName.toLocaleLowerCase()}`)
        ?? "";

      if (!categoryId) {
        throw new Error(`Profile data import could not resolve category "${categoryName}" for year "${year}".`);
      }

      expensesStore.put({
        id: crypto.randomUUID(),
        profileId,
        year,
        date: String(row?.date ?? "").trim(),
        amount: Number(row?.amount ?? 0),
        method: String(row?.method ?? "").trim(),
        type: String(row?.type ?? "").trim(),
        supplier: String(row?.supplier ?? "").trim(),
        categoryId,
        category: categoryName,
        service: String(row?.service ?? "").trim(),
        createdAt: now,
        updatedAt: now
      });
    });
  });

  await transactionDone(transaction);

  const scope = options.activate === false
    ? { profileId, year: profileData.activeYear }
    : await setActiveBudgetScope(profileId, profileData.activeYear);

  return {
    scope,
    profile: normalizeProfileRecord({
      id: profileId,
      name: profileName,
      source: options.source ?? "import",
      isDefault: false,
      archivedAt: null,
      createdAt: now,
      updatedAt: now
    })
  };
}
